import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Plus, Minus, Send, ShoppingCart,
  ImageIcon, X, Check, UserPlus, Trash2, Users,
} from "lucide-react";

const TAX_RATE = 0.085; // 8.5% — standard California rate

function calcTax(subtotal) {
  return Math.round(subtotal * TAX_RATE * 100) / 100;
}

export default function TruckOrderPage() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCart, setShowCart] = useState(false);
  const [sending, setSending] = useState(false);
  const [orderNotes, setOrderNotes] = useState("");
  const [showPhoto, setShowPhoto] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [orderSent, setOrderSent] = useState(null);

  // Multi-person cart: array of { name, items[] }
  const [persons, setPersons] = useState([{ name: "", items: [] }]);
  const [activePersonIdx, setActivePersonIdx] = useState(0);

  const fetchMenu = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/menu/${businessId}`);
      setMenu(res.data);
    } catch {
      toast.error("Failed to load menu");
    }
    setLoading(false);
  }, [businessId]);

  useEffect(() => { fetchMenu(); }, [fetchMenu]);

  const loadPhoto = async () => {
    if (photoUrl) { setShowPhoto(!showPhoto); return; }
    setPhotoLoading(true);
    try {
      const res = await axios.get(`${API}/menu/photo/${businessId}`, { responseType: "blob" });
      setPhotoUrl(URL.createObjectURL(res.data));
      setShowPhoto(true);
    } catch { toast.error("Menu photo not available"); }
    setPhotoLoading(false);
  };

  // Add item to the active person's cart
  const addToCart = (item, customizations = {}, specialInstructions = "") => {
    setPersons((prev) => {
      const updated = [...prev];
      const person = { ...updated[activePersonIdx] };
      const existingIdx = person.items.findIndex(
        (c) => c.item_id === item.item_id && JSON.stringify(c.customizations) === JSON.stringify(customizations)
      );
      if (existingIdx >= 0) {
        person.items = [...person.items];
        person.items[existingIdx] = { ...person.items[existingIdx], quantity: person.items[existingIdx].quantity + 1 };
      } else {
        person.items = [...person.items, {
          item_id: item.item_id,
          name: item.name,
          unit_price: item.price || 0,
          quantity: 1,
          customizations,
          special_instructions: specialInstructions,
        }];
      }
      updated[activePersonIdx] = person;
      return updated;
    });
    toast.success(`${item.name} added for ${persons[activePersonIdx].name || `Person ${activePersonIdx + 1}`}`);
  };

  const updateQty = (personIdx, itemIdx, delta) => {
    setPersons((prev) => {
      const updated = [...prev];
      const person = { ...updated[personIdx], items: [...updated[personIdx].items] };
      person.items[itemIdx] = { ...person.items[itemIdx], quantity: Math.max(0, person.items[itemIdx].quantity + delta) };
      if (person.items[itemIdx].quantity === 0) person.items.splice(itemIdx, 1);
      updated[personIdx] = person;
      return updated;
    });
  };

  const addPerson = () => {
    setPersons((prev) => [...prev, { name: "", items: [] }]);
    setActivePersonIdx(persons.length);
    toast.success("Added another person to the order");
  };

  const removePerson = (idx) => {
    if (persons.length <= 1) return;
    setPersons((prev) => prev.filter((_, i) => i !== idx));
    setActivePersonIdx((prev) => Math.min(prev, persons.length - 2));
  };

  const updatePersonName = (idx, name) => {
    setPersons((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], name };
      return updated;
    });
  };

  // Calculate per-person and grand totals
  const personTotals = persons.map((p) => {
    const subtotal = p.items.reduce((sum, c) => sum + c.unit_price * c.quantity, 0);
    const tax = calcTax(subtotal);
    return { subtotal: Math.round(subtotal * 100) / 100, tax, total: Math.round((subtotal + tax) * 100) / 100 };
  });
  const grandSubtotal = personTotals.reduce((s, t) => s + t.subtotal, 0);
  const grandTax = personTotals.reduce((s, t) => s + t.tax, 0);
  const grandTotal = Math.round((grandSubtotal + grandTax) * 100) / 100;
  const totalItems = persons.reduce((s, p) => s + p.items.reduce((ss, i) => ss + i.quantity, 0), 0);

  const sendOrder = async () => {
    // Validate all persons have names
    const emptyNames = persons.filter((p, i) => !p.name.trim() && p.items.length > 0);
    if (emptyNames.length > 0) {
      toast.error("Please enter a name for everyone with items");
      return;
    }
    // Filter out empty persons
    const filledPersons = persons.filter((p) => p.items.length > 0);
    if (filledPersons.length === 0) {
      toast.error("Add at least one item");
      return;
    }

    setSending(true);
    try {
      const res = await axios.post(`${API}/orders/place`, {
        business_id: businessId,
        persons: filledPersons.map((p, i) => ({
          name: p.name.trim(),
          items: p.items,
          subtotal: personTotals[persons.indexOf(p)].subtotal,
          tax: personTotals[persons.indexOf(p)].tax,
          person_total: personTotals[persons.indexOf(p)].total,
        })),
        notes: orderNotes,
        tax_rate: TAX_RATE,
      });
      setOrderSent(res.data);
      setPersons([{ name: "", items: [] }]);
      setActivePersonIdx(0);
      setOrderNotes("");
      setShowCart(false);
      toast.success("Order sent!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send order");
    }
    setSending(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-6 h-6 animate-spin text-sky-500" />
      </div>
    );
  }

  if (!menu || !menu.has_menu) {
    return (
      <div className="min-h-screen px-4 pt-14 pb-24 bg-white">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-gray-400 text-xs mb-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="text-center py-16">
          <ShoppingCart className="w-10 h-10 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">This truck hasn't set up digital ordering yet</p>
        </div>
      </div>
    );
  }

  // Order success view
  if (orderSent) {
    return (
      <div className="min-h-screen px-4 pt-14 pb-24 bg-white" data-testid="order-success">
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Order Sent!</h2>
          <p className="text-gray-500 text-sm mb-4">Your order has been sent to {menu.truck_name}</p>

          <div className="bg-gray-50 rounded-2xl p-4 mb-4 inline-block">
            <p className="text-gray-400 text-[10px] uppercase tracking-wider mb-1">Order Number</p>
            <p className="text-3xl font-black text-gray-900" data-testid="order-display-number">#{orderSent.display_number}</p>
          </div>

          {/* Per-person breakdown */}
          {orderSent.persons && orderSent.persons.length > 0 && (
            <div className="bg-gray-50 rounded-2xl p-4 mb-4 text-left max-w-sm mx-auto">
              {orderSent.persons.map((p, i) => (
                <div key={i} className={`${i > 0 ? "mt-3 pt-3 border-t border-gray-200" : ""}`}>
                  <p className="text-sm font-bold text-gray-900">{p.name}</p>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Subtotal</span>
                    <span>${p.subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Tax</span>
                    <span>${p.tax?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-bold text-gray-900 mt-0.5">
                    <span>{p.name} owes</span>
                    <span>${p.person_total?.toFixed(2)}</span>
                  </div>
                </div>
              ))}
              <div className="mt-3 pt-3 border-t-2 border-gray-300 flex justify-between text-base font-black text-gray-900">
                <span>Grand Total</span>
                <span>${orderSent.grand_total?.toFixed(2)}</span>
              </div>
            </div>
          )}

          <p className="text-gray-400 text-xs mb-6">Show this number when picking up</p>

          <div className="space-y-2">
            <button
              onClick={() => setOrderSent(null)}
              className="w-full py-3 rounded-xl bg-sky-500 text-white text-sm font-bold"
              data-testid="order-another-btn"
            >
              Order More
            </button>
            <button onClick={() => navigate(-1)} className="w-full py-3 rounded-xl bg-gray-100 text-gray-600 text-sm font-medium">
              Back to Truck
            </button>
          </div>
        </div>
      </div>
    );
  }

  const categories = [...new Set(menu.items.map((i) => i.category || "Mains"))];

  return (
    <div className="min-h-screen bg-white pb-32" data-testid="truck-order-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 py-3">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-gray-400 text-xs">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-sm font-bold text-gray-900">{menu.truck_name}</h1>
          <button onClick={loadPhoto} className="text-sky-500 text-[10px] font-bold flex items-center gap-1" data-testid="view-menu-photo-btn">
            {photoLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
            Menu Photo
          </button>
        </div>
      </div>

      {/* Menu photo overlay */}
      {showPhoto && photoUrl && (
        <div className="px-4 py-2 bg-gray-50">
          <div className="relative">
            <img src={photoUrl} alt="Menu board" className="w-full rounded-xl shadow-sm" data-testid="menu-photo-display" />
            <button onClick={() => setShowPhoto(false)} className="absolute top-2 right-2 bg-black/60 text-white p-1 rounded-full">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Person tabs */}
      <div className="sticky top-[49px] z-10 bg-white border-b border-gray-100 px-4 py-2">
        <div className="flex items-center gap-2 overflow-x-auto">
          {persons.map((p, i) => (
            <button
              key={i}
              onClick={() => setActivePersonIdx(i)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold transition flex items-center gap-1.5 ${
                activePersonIdx === i
                  ? "bg-sky-500 text-white"
                  : "bg-gray-100 text-gray-500"
              }`}
              data-testid={`person-tab-${i}`}
            >
              {p.name || `Person ${i + 1}`}
              {p.items.length > 0 && (
                <span className={`text-[9px] px-1 py-0.5 rounded-full ${
                  activePersonIdx === i ? "bg-white/20" : "bg-gray-200"
                }`}>
                  {p.items.reduce((s, item) => s + item.quantity, 0)}
                </span>
              )}
            </button>
          ))}
          <button
            onClick={addPerson}
            className="shrink-0 w-7 h-7 rounded-full bg-sky-100 text-sky-600 flex items-center justify-center hover:bg-sky-200 transition"
            data-testid="add-person-btn"
          >
            <UserPlus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Menu items */}
      <div className="px-4 pt-3">
        {categories.map((cat) => (
          <div key={cat} className="mb-5" data-testid={`order-category-${cat.toLowerCase()}`}>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{cat}</h3>
            {menu.items
              .filter((item) => (item.category || "Mains") === cat && !item.sold_out)
              .map((item) => (
                <MenuItemCard key={item.item_id} item={item} onAdd={addToCart} />
              ))}
            {menu.items.filter((item) => (item.category || "Mains") === cat && item.sold_out).length > 0 && (
              <div className="mt-1">
                {menu.items
                  .filter((item) => (item.category || "Mains") === cat && item.sold_out)
                  .map((item) => (
                    <div key={item.item_id} className="opacity-40 py-2 px-3 rounded-lg">
                      <p className="text-sm text-gray-500 line-through">{item.name}</p>
                      <span className="text-[9px] text-red-400 font-bold">SOLD OUT</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Cart bar */}
      {totalItems > 0 && !showCart && (
        <div className="fixed bottom-0 left-0 right-0 z-20 max-w-md mx-auto">
          <button
            onClick={() => setShowCart(true)}
            className="w-full bg-sky-500 text-white py-4 px-6 flex items-center justify-between shadow-lg"
            data-testid="open-cart-btn"
          >
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              <span className="font-bold text-sm">
                {totalItems} item{totalItems !== 1 ? "s" : ""}
                {persons.filter((p) => p.items.length > 0).length > 1 &&
                  ` · ${persons.filter((p) => p.items.length > 0).length} people`}
              </span>
            </div>
            <span className="font-bold">${grandTotal.toFixed(2)}</span>
          </button>
        </div>
      )}

      {/* Cart drawer — multi-person */}
      {showCart && (
        <div className="fixed inset-0 z-30 bg-black/50 flex items-end" onClick={() => setShowCart(false)}>
          <div
            className="w-full max-w-md mx-auto bg-white rounded-t-3xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="cart-drawer"
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between z-10">
              <h2 className="font-bold text-gray-900 flex items-center gap-2">
                Your Order
                {persons.filter((p) => p.items.length > 0).length > 1 && (
                  <span className="text-[10px] bg-sky-100 text-sky-600 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                    <Users className="w-3 h-3" /> {persons.filter((p) => p.items.length > 0).length} people
                  </span>
                )}
              </h2>
              <button onClick={() => setShowCart(false)} className="text-gray-400 p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Per-person sections */}
            {persons.map((person, pIdx) => {
              if (person.items.length === 0 && persons.length > 1) return null;
              const totals = personTotals[pIdx];
              return (
                <div key={pIdx} className={`px-4 py-3 ${pIdx > 0 ? "border-t-2 border-sky-100" : ""}`} data-testid={`cart-person-${pIdx}`}>
                  {/* Person name */}
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1">
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                        {persons.length > 1 ? `Person ${pIdx + 1}` : "Your Name"}
                      </label>
                      <input
                        value={person.name}
                        onChange={(e) => updatePersonName(pIdx, e.target.value.slice(0, 50))}
                        placeholder="Enter name"
                        className="w-full bg-gray-50 text-gray-900 text-sm font-medium px-3 py-2 rounded-lg outline-none border border-gray-200 focus:border-sky-400 transition mt-1"
                        data-testid={`person-name-${pIdx}`}
                        autoComplete="given-name"
                      />
                    </div>
                    {persons.length > 1 && (
                      <button
                        onClick={() => removePerson(pIdx)}
                        className="mt-5 p-1.5 text-red-300 hover:text-red-500 transition"
                        data-testid={`remove-person-${pIdx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Items */}
                  {person.items.length === 0 ? (
                    <p className="text-gray-300 text-xs text-center py-3">No items yet — select from the menu above</p>
                  ) : (
                    <div className="space-y-1.5">
                      {person.items.map((item, iIdx) => (
                        <div key={iIdx} className="flex items-center justify-between py-1.5">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{item.name}</p>
                            {Object.keys(item.customizations || {}).length > 0 && (
                              <p className="text-[10px] text-gray-400 mt-0.5">
                                {Object.entries(item.customizations).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`).join(" | ")}
                              </p>
                            )}
                            {item.special_instructions && (
                              <p className="text-[10px] text-amber-600 mt-0.5">"{item.special_instructions}"</p>
                            )}
                            <p className="text-xs text-gray-400 mt-0.5">${(item.unit_price * item.quantity).toFixed(2)}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => updateQty(pIdx, iIdx, -1)} className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center" data-testid={`cart-minus-${pIdx}-${iIdx}`}>
                              <Minus className="w-3.5 h-3.5 text-gray-600" />
                            </button>
                            <span className="text-sm font-bold text-gray-900 w-4 text-center">{item.quantity}</span>
                            <button onClick={() => updateQty(pIdx, iIdx, 1)} className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center" data-testid={`cart-plus-${pIdx}-${iIdx}`}>
                              <Plus className="w-3.5 h-3.5 text-gray-600" />
                            </button>
                          </div>
                        </div>
                      ))}

                      {/* Per-person subtotal with tax */}
                      <div className="bg-gray-50 rounded-lg px-3 py-2 mt-2" data-testid={`person-total-${pIdx}`}>
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Subtotal</span>
                          <span>${totals.subtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Tax (8.5%)</span>
                          <span>${totals.tax.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm font-bold text-gray-900 mt-1 pt-1 border-t border-gray-200">
                          <span>{person.name || `Person ${pIdx + 1}`} owes</span>
                          <span>${totals.total.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Add another person */}
            <div className="px-4 py-2">
              <button
                onClick={addPerson}
                className="w-full py-2 rounded-lg border-2 border-dashed border-sky-200 text-sky-500 text-xs font-bold flex items-center justify-center gap-1.5 hover:bg-sky-50 transition"
                data-testid="cart-add-person-btn"
              >
                <UserPlus className="w-3.5 h-3.5" /> Add Another Person
              </button>
            </div>

            {/* Notes */}
            <div className="px-4 py-2">
              <textarea
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value.slice(0, 500))}
                placeholder="Special instructions for the whole order..."
                className="w-full bg-gray-50 text-gray-700 text-xs px-3 py-2.5 rounded-xl outline-none border border-gray-100 resize-none"
                rows={2}
                data-testid="order-notes-input"
              />
            </div>

            {/* Grand total + Send */}
            <div className="px-4 py-4 border-t-2 border-gray-200 bg-gray-50">
              {persons.filter((p) => p.items.length > 0).length > 1 && (
                <div className="mb-2">
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Subtotal ({persons.filter((p) => p.items.length > 0).length} people)</span>
                    <span>${grandSubtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Total Tax</span>
                    <span>${grandTax.toFixed(2)}</span>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between mb-3">
                <span className="text-gray-900 text-sm font-bold">Grand Total</span>
                <span className="text-xl font-black text-gray-900" data-testid="cart-total">${grandTotal.toFixed(2)}</span>
              </div>
              <button
                onClick={sendOrder}
                disabled={sending || totalItems === 0}
                className="w-full py-3.5 rounded-xl bg-sky-500 text-white text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="send-order-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send Order</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Individual menu item card with customization support.
 */
function MenuItemCard({ item, onAdd }) {
  const [expanded, setExpanded] = useState(false);
  const [selections, setSelections] = useState({});
  const [specialInstructions, setSpecialInstructions] = useState("");

  const hasCustomizations = (item.customizations || []).length > 0;

  const handleQuickAdd = () => {
    if (hasCustomizations) {
      setExpanded(true);
    } else {
      onAdd(item, {}, "");
    }
  };

  const handleAddWithOptions = () => {
    onAdd(item, selections, specialInstructions);
    setExpanded(false);
    setSelections({});
    setSpecialInstructions("");
  };

  return (
    <div className="bg-gray-50 rounded-xl p-3 mb-2" data-testid={`menu-order-item-${item.item_id}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1" onClick={handleQuickAdd}>
          <p className="text-sm font-medium text-gray-900">{item.name}</p>
          {item.description && <p className="text-[11px] text-gray-400 mt-0.5">{item.description}</p>}
          <div className="flex items-center gap-2 mt-1">
            {item.price > 0 && <span className="text-sky-600 text-xs font-bold">${item.price.toFixed(2)}</span>}
            {hasCustomizations && <span className="text-gray-300 text-[10px]">Customizable</span>}
          </div>
        </div>
        <button
          onClick={handleQuickAdd}
          className="shrink-0 w-8 h-8 rounded-full bg-sky-500 text-white flex items-center justify-center hover:bg-sky-600 transition"
          data-testid={`add-item-${item.item_id}`}
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-gray-200 space-y-3">
          {(item.customizations || []).map((cust, ci) => (
            <div key={ci}>
              <p className="text-[11px] font-bold text-gray-600 mb-1.5">
                {cust.name} {cust.required && <span className="text-red-400">*</span>}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {(cust.options || []).map((opt) => {
                  const isSelected = Array.isArray(selections[cust.name])
                    ? selections[cust.name].includes(opt)
                    : selections[cust.name] === opt;
                  return (
                    <button
                      key={opt}
                      onClick={() => {
                        if (cust.required) {
                          setSelections((s) => ({ ...s, [cust.name]: opt }));
                        } else {
                          setSelections((s) => {
                            const current = Array.isArray(s[cust.name]) ? s[cust.name] : [];
                            return { ...s, [cust.name]: current.includes(opt) ? current.filter((o) => o !== opt) : [...current, opt] };
                          });
                        }
                      }}
                      className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium border transition ${
                        isSelected ? "bg-sky-500 text-white border-sky-500" : "bg-white text-gray-600 border-gray-200 hover:border-sky-300"
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          <div>
            <p className="text-[11px] font-bold text-gray-600 mb-1.5">Special Instructions</p>
            <input
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              placeholder="e.g., no onions, extra salsa..."
              className="w-full bg-white text-gray-700 text-xs px-3 py-2 rounded-lg border border-gray-200 outline-none"
              data-testid={`special-instructions-${item.item_id}`}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleAddWithOptions}
              className="flex-1 py-2 rounded-lg bg-sky-500 text-white text-xs font-bold flex items-center justify-center gap-1"
              data-testid={`confirm-add-${item.item_id}`}
            >
              <Plus className="w-3.5 h-3.5" /> Add to Order
            </button>
            <button onClick={() => setExpanded(false)} className="px-3 py-2 rounded-lg bg-gray-100 text-gray-500 text-xs">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
